import type { RequestContext } from '@shared/types/RequestContext.ts';
import type { EvergladesProductsApiSuccess, IntentResult, SearchContext } from '@shared/types/types.ts';
import { isContextIntent, isInterleavedIntent, isRankedIntent, isSponsoredProduct } from '@shared/types/types.ts';
import { getLayout } from '@tilelist/domain/composition/BracketLayout.ts';
import { getPagination } from '@tilelist/domain/composition/Pagination.ts';
import { getRankedPaging } from '@tilelist/domain/composition/PaginationMeta.ts';
import {
    enrichSearchProvidedInterleavedProducts,
    interleaveRankedAndSponsoredProducts,
} from '@tilelist/domain/composition/RankedPositioning.ts';
import { appendSimilarProducts } from '@tilelist/domain/composition/SimilarProducts.ts';
import type { ProductItem, TileListItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { insertBenefitCinemaSlots } from '@tilelist/domain/feature-visibility/BenefitCinemaSlot.ts';
import { insertProductComparisonBanner } from '@tilelist/domain/feature-visibility/ComparisonBanner.ts';
import { extractComparisonId } from '@tilelist/domain/feature-visibility/ComparisonService.ts';
import { insertLotteryCommunication } from '@tilelist/domain/feature-visibility/LotteryCommunication.ts';
import { insertMASBannerTargets } from '@tilelist/domain/feature-visibility/MASBanner.ts';
import { insertSDABillboard } from '@tilelist/domain/feature-visibility/SDABillboard.ts';
import { composeBaseTrackingLabels } from '@tilelist/domain/tracking/BaseTracking.ts';

type TileListPageCompositionQuery = {
    c?: string;
    l?: 'gp' | 'gl' | 'gq';
};

/**
 *
 *
 */
function resolveIntentCounts(intents: IntentResult[]) {
    const interleavedIntent = intents.find(isInterleavedIntent);
    const rankedIntent = intents.find(isRankedIntent);
    const sponsoredIntent = intents.find((i) => i.intent === 'sponsored');
    const primaryIntent = interleavedIntent ?? rankedIntent;
    return {
        rankedCount: interleavedIntent?.count ?? rankedIntent?.count ?? 0,
        sponsoredCount: interleavedIntent ? 0 : (sponsoredIntent?.count ?? 0),
        organicCount: interleavedIntent
            ? interleavedIntent.products.filter((product) => !isSponsoredProduct(product)).length
            : undefined,
        displayCount: primaryIntent?.displayCount ?? 0,
        sortOrder: primaryIntent?.meta?.sortOrder,
    };
}

/**
 *
 *
 *
 */
export function buildPageLayout(
    response: EvergladesProductsApiSuccess,
    requestContext?: RequestContext,
    queryParams?: TileListPageCompositionQuery,
    onex?: OnEx,
    boostedItems?: ProductItem[],
): TileListPageLayout {
    const rankedIntent = response.intents?.find(isRankedIntent);
    const interleavedIntent = response.intents?.find(isInterleavedIntent);

    const primaryIntent = interleavedIntent ?? rankedIntent;
    const isFallback = !primaryIntent || primaryIntent.count === 0;
    const searchContext = extractSearchContext(response);
    const comparisonIdOrNull = extractComparisonId(searchContext, queryParams?.c);
    const comparisonId = comparisonIdOrNull || undefined;
    const layout = getLayout(response.intents, queryParams?.l, onex);

    /**
 *
 *
 *
 *
 */
    const isMobile = requestContext?.isMobile ?? false;

    const context = {
        rule: response.rule ?? '',
        searchContext,
        layout,
    };

    const page = getRankedPaging(response.intents, layout, comparisonId);
    const { rankedCount, sponsoredCount, organicCount, displayCount, sortOrder } = resolveIntentCounts(
        response.intents,
    );
    const intentCounts = { rankedCount, sponsoredCount };

    const { currentPage, currentOffset, lastPage } = getPagination(intentCounts, page);

    const pagination = {
        currentPage,
        currentOffset,
        lastPage,
    };

    const tracking = composeBaseTrackingLabels(
        response.intents,
        isMobile,
        searchContext,
        sortOrder,
        currentPage,
        lastPage,
        onex,
    );

    const tileListItems = composeTileListItems(response.intents, comparisonId, onex, searchContext, boostedItems);

    return {
        tileListItems,
        tracking,
        page,
        isFallback,
        pagination,
        rankedCount,
        sponsoredCount,
        organicCount,
        displayCount,
        comparisonId,
        context,
    };
}

/**
 *
 *
 *
 *
 *
 */
function composeTileListItems(
    intents: IntentResult[],
    comparisonId?: string,
    onex?: OnEx,
    searchContext?: SearchContext,
    boostedItems?: ProductItem[],
): TileListItem[] {
    const interleavedIntent = intents.find(isInterleavedIntent);
    const interleavedProducts = interleavedIntent
        ? enrichSearchProvidedInterleavedProducts(interleavedIntent)
        : interleaveRankedAndSponsoredProducts(intents);
    const productItems = prependBoostedItems(boostedItems, interleavedProducts);
    const lastItem = productItems.at(-1);
    const lastOffset = lastItem?.localListPosition ?? 0;

    let tileListItems = insertProductComparisonBanner(productItems, comparisonId, onex);
    tileListItems = insertMASBannerTargets(tileListItems);
    tileListItems = insertSDABillboard(tileListItems, searchContext, onex);
    tileListItems = insertLotteryCommunication(tileListItems, searchContext?.rule, onex);
    tileListItems = insertBenefitCinemaSlots(tileListItems, onex);
    tileListItems = appendSimilarProducts(intents, tileListItems, lastOffset);
    return tileListItems;
}

function prependBoostedItems(boostedItems: ProductItem[] | undefined, products: ProductItem[]): ProductItem[] {
    if (!boostedItems || boostedItems.length === 0) return products;
    const offset = boostedItems.length;
    const renumbered = products.map((item) => ({
        ...item,
        localListPosition: item.localListPosition + offset,
        actualListPosition: item.actualListPosition + offset,
    }));
    return [...boostedItems, ...renumbered];
}

function extractSearchContext(response: EvergladesProductsApiSuccess): SearchContext {
    const context = response.intents.find(isContextIntent) || {};
    return {
        rule: response.rule,
        ...context,
    };
}
